import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { paymentService, SubscriptionStatus, UploadLimits, Features } from '../../../services/paymentService';
import { toastHelper } from '../../../utils/toastHelper';
import { SubscriptionLimit, UnifiedPlan, LifetimeStatus } from '../types';
import { useUserSubscription } from '../../../contexts/UserSubscriptionContext';
import { useUploadDefaults } from '../../../contexts/AppDataContext';

declare global {
  interface Window {
    paypal?: {
      Buttons: (config: {
        createSubscription: (data: any, actions: any) => Promise<any>;
        onApprove: (data: any, actions: any) => void;
        style?: {
          layout?: string;
          color?: string;
          shape?: string;
          label?: string;
        };
      }) => {
        render: (selector: string) => void;
      };
    };
  }
}

export interface UpgradeProration {
  prorated_amount: string;
  old_tier: string;
  new_tier: string;
  old_price: string;
  new_price: string;
  proration_price?: string;
  old_billing_cycle?: string;
  new_billing_cycle?: string;
  days_remaining: number;
  total_days: number;
  days_used?: number;
  billing_cycle_end_date: string | null;
  currency: string;
  prorated_new_plan_charge: string;
  prorated_old_plan_credit: string;
  full_new_plan_price?: string;
  already_paid?: string;
  remaining_days_prorate?: string;
  net_credit?: string;
  is_free_upgrade?: boolean;
  free_upgrade_promotion_amount?: string;
  total_due_today?: string;
}

export interface SubscriptionDataViewModel {
  // Data
  subscriptionStatus: SubscriptionStatus | null;
  subscriptionPlans: SubscriptionPlansResponse | null;
  uploadLimits: UploadLimits | null;
  features: Features | null;
  lifetimeStatus: LifetimeStatus | null;
  unifiedPlans: UnifiedPlan[];
  tierLimits: Map<string, SubscriptionLimit>;
  paypalClientId: string | null;
  paypalSdkLoaded: boolean;
  paypalMounted: boolean;

  // UI State
  loading: boolean;
  error: string | null;
  countdown: number;
  syncingSubscription: boolean;
  activeTab: string;

  // Derived values
  tier: string;
  tierLabel: string;
  activeTier: string;
  activeBillingCycle: string | null;
  hasPaidSubscription: boolean;
  hasRoleBasedAccess: boolean;
  hasPendingDowngrade: boolean;
  hasPendingCancellation: boolean;
  hasPendingCancellationToFree: boolean;
  currentPlanWithFreeUpgrade: any | null;
  canUseFreeUpgrade: boolean;
  getInitialCarouselIndex: number;
  isFamilyMember: boolean;

  // Actions
  fetchSubscriptionData: () => Promise<void>;
  handleTabChange: (value: string) => void;
}

export function useSubscriptionData(): SubscriptionDataViewModel {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    subscriptionStatus: contextSubscriptionStatus,
    uploadLimits: contextUploadLimits,
    tierLimits: contextTierLimits,
    isFamilyMember,
    refresh: refreshUserSubscription
  } = useUserSubscription();
  
  const { refresh: refreshAppData } = useUploadDefaults();
  
  // Data state
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [subscriptionPlans] = useState<null>(null); // Endpoint not yet implemented
  const [uploadLimits, setUploadLimits] = useState<UploadLimits | null>(null);
  const [features, setFeatures] = useState<Features | null>(null);
  const [lifetimeStatus, setLifetimeStatus] = useState<LifetimeStatus | null>(null);
  const [unifiedPlans, setUnifiedPlans] = useState<UnifiedPlan[]>([]);
  const [tierLimits, setTierLimits] = useState<Map<string, SubscriptionLimit>>(new Map());
  const [paypalClientId, setPaypalClientId] = useState<string | null>(null);
  const [paypalSdkLoaded, setPaypalSdkLoaded] = useState<boolean>(false);
  const [paypalMounted, setPaypalMounted] = useState<boolean>(false);
  
  // UI state
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const [syncingSubscription, setSyncingSubscription] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_monthlyCarouselApi, _setMonthlyCarouselApi] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_annualCarouselApi, _setAnnualCarouselApi] = useState<any>(null);
  
  // Determine active tab from URL
  const getActiveTab = useCallback(() => {
    const path = location.pathname;
    if (path.endsWith('/lifetime')) return 'lifetime';
    if (path.endsWith('/annual')) return 'annual';
    if (path.endsWith('/monthly')) return 'monthly';
    return 'lifetime'; // default to lifetime (focused like Pro tier)
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState<string>(getActiveTab());

  // Update URL when tab changes
  const handleTabChange = useCallback((value: string) => {
    try {
      setActiveTab(value);
      const basePath = '/subscriptions';
      if (value === 'lifetime') {
        navigate(`${basePath}/lifetime`, { replace: true });
      } else if (value === 'annual') {
        navigate(`${basePath}/annual`, { replace: true });
      } else {
        navigate(`${basePath}/monthly`, { replace: true });
      }
    } catch (error) {
      // Silently handle tab change errors
    }
  }, [navigate]);

  // Sync tab with URL on location change and redirect if needed
  useEffect(() => {
    const path = location.pathname;
    if (path === '/subscriptions') {
      // Redirect to lifetime by default (focused like Pro tier)
      navigate('/subscriptions/lifetime', { replace: true });
      setActiveTab('lifetime');
    } else {
      const tab = getActiveTab();
      setActiveTab(tab);
    }
  }, [location.pathname, navigate, getActiveTab]);

  const fetchSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Refresh user subscription context (includes permissions, limits, features)
      // This ensures the UI gets the latest subscription status after purchases/upgrades
      await refreshUserSubscription();

      // Fetch unified app plans from backend
      // NOTE: Subscription plans are NOT cached because admins can change them dynamically.
      // They must always be fetched fresh to ensure users see the latest pricing and plans.
      const activePlansData = await paymentService.getActiveSubscriptionPlans();
      setPaypalClientId(activePlansData.paypal_client_id);

      // Ensure we have the current plan with all tiers
      if (activePlansData.unified_plans && activePlansData.unified_plans.length > 0) {
        setUnifiedPlans(activePlansData.unified_plans);
      } else {
        setUnifiedPlans([]);
      }

      // Note: Tier limits are now fetched from UserSubscriptionContext and will be set via useEffect
      // They are cached because they represent static configuration that rarely changes.

      // Note: subscription status and upload limits are now fetched from UserSubscriptionContext
      // They will be set via useEffect that watches the context

      // Note: subscriptionPlans endpoint is not yet implemented
      // These features are conditionally rendered, so they won't break if null

      // Fetch lifetime status (authenticated - includes user ownership info)
      try {
        const lifetimeData = await paymentService.getLifetimeStatusAuthenticated();
        setLifetimeStatus(lifetimeData);
      } catch (lifetimeErr: any) {
        // Don't fail the whole page if lifetime status fails - it's optional
        console.warn('Failed to fetch lifetime status:', lifetimeErr);
        setLifetimeStatus(null);
      }
    } catch (err: any) {
      // Only show error if it's a critical failure
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load subscription data';
      // Don't show toast for missing endpoints - they'll be implemented gradually
      if (err.response?.status !== 404) {
        setError(errorMessage);
        toastHelper.newMessage('error', 'Error', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [refreshUserSubscription]);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  // Redirect away from lifetime tab if lifetime is not available
  useEffect(() => {
    if (!loading && lifetimeStatus === null && activeTab === 'lifetime') {
      // Lifetime not available, redirect to monthly
      navigate('/subscriptions/monthly', { replace: true });
      setActiveTab('monthly');
    }
  }, [loading, lifetimeStatus, activeTab, navigate]);

  // Sync subscription status and upload limits from UserSubscriptionContext
  useEffect(() => {
    if (contextSubscriptionStatus) {
      setSubscriptionStatus(contextSubscriptionStatus);
      // Construct Features object from subscription status - features are now dynamic
      if (contextSubscriptionStatus.features) {
        setFeatures({
          subscription_tier: contextSubscriptionStatus.subscription_tier,
          daily_upload_limit: undefined, // Not available from subscription status
          max_accounts: undefined,
          is_complimentary: contextSubscriptionStatus.is_complimentary || false,
          // Spread all dynamic features from API
          ...contextSubscriptionStatus.features,
        });
      }
    }
  }, [contextSubscriptionStatus]);

  useEffect(() => {
    if (contextUploadLimits) {
      setUploadLimits(contextUploadLimits);
      // Update features with upload limit data if available - features are now dynamic
      if (contextUploadLimits.features && subscriptionStatus) {
        setFeatures({
          subscription_tier: subscriptionStatus.subscription_tier,
          daily_upload_limit: contextUploadLimits.instant_limit !== null && contextUploadLimits.instant_limit !== undefined ? contextUploadLimits.instant_limit : undefined,
          max_accounts: undefined,
          is_complimentary: subscriptionStatus.is_complimentary || false,
          // Spread all dynamic features from API
          ...contextUploadLimits.features,
        });
      }
    }
  }, [contextUploadLimits, subscriptionStatus]);

  // Sync tier limits from UserSubscriptionContext
  useEffect(() => {
    if (contextTierLimits) {
      setTierLimits(contextTierLimits);
    } else {
      // If context doesn't have tier limits yet, use empty map
      setTierLimits(new Map());
    }
  }, [contextTierLimits]);

  // Check for subscription revision query parameters on page load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const revisionStatus = params.get('subscription_revision');
    const revisionType = params.get('type');

    if (revisionStatus === 'success') {
      // Show appropriate toast based on type
      if (revisionType === 'upgrade') {
        toastHelper.newMessage(
          'success',
          'Upgrade Successful',
          'Your subscription has been upgraded successfully. You now have access to all premium features.'
        );
      } else if (revisionType === 'downgrade') {
        toastHelper.newMessage(
          'success',
          'Downgrade Scheduled',
          'Your subscription downgrade has been scheduled. The changes will take effect at your next billing cycle.'
        );
      } else {
        toastHelper.newMessage(
          'success',
          'Success',
          'Your subscription has been updated successfully.'
        );
      }
      
      // Handle upgrade: refresh all cached contexts since tier/permissions change immediately
      if (revisionType === 'upgrade') {
        const syncAndRefresh = async () => {
          setSyncingSubscription(true);
          try {
            await paymentService.syncSubscription();
            
            // Refresh all cached contexts to reflect new subscription state
            await Promise.all([
              refreshUserSubscription(), // Refreshes subscription status, upload limits, tier limits
              refreshAppData(),          // Refreshes upload defaults (may change with tier)
              fetchSubscriptionData()    // Refreshes subscription plans (not cached)
            ]);
          } catch (error: any) {
            // Even if sync fails, refresh anyway (webhook will catch it)
            console.error('Sync failed, refreshing anyway:', error);
            await Promise.all([
              refreshUserSubscription(),
              refreshAppData(),
              fetchSubscriptionData()
            ]);
          } finally {
            setSyncingSubscription(false);
          }
        };
        
        syncAndRefresh();
      } else if (revisionType === 'downgrade') {
        // Downgrade: only sync, no need to refresh cached data
        // Current tier stays active until next billing cycle, so permissions/limits don't change
        const syncOnly = async () => {
          setSyncingSubscription(true);
          try {
            await paymentService.syncSubscription();
            // Just refresh subscription plans to show updated pending downgrade info
            await fetchSubscriptionData();
          } catch (error: any) {
            console.error('Sync failed:', error);
            await fetchSubscriptionData();
          } finally {
            setSyncingSubscription(false);
          }
        };
        
        syncOnly();
      } else {
        // For other types, just refresh subscription plans
        fetchSubscriptionData();
      }
      
      // Clean up URL by removing query params
      navigate(location.pathname, { replace: true });
    } else if (revisionStatus === 'cancelled') {
      toastHelper.newMessage(
        'error',
        'Cancelled',
        'Subscription revision was cancelled'
      );
      
      // Clean up URL by removing query params
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname, refreshAppData, refreshUserSubscription, fetchSubscriptionData]);

  // Handle PayPal SDK mounting
  useEffect(() => {
    setPaypalMounted(true);
  }, []);

  // Load PayPal SDK (only once, as per PayPal documentation)
  useEffect(() => {
    if (!paypalClientId || paypalMounted === false) return;

    // Check if SDK is already loaded
    if (window.paypal) {
      setPaypalSdkLoaded(true);
      return;
    }

    // Check if script already exists in DOM (avoid duplicate loading)
    const existingScript = document.querySelector(`script[src*="paypal.com/sdk"]`);
    if (existingScript) {
      // Wait for it to load
      if (window.paypal) {
        setPaypalSdkLoaded(true);
      } else {
        existingScript.addEventListener('load', () => setPaypalSdkLoaded(true));
      }
      return;
    }
    
    // Load PayPal SDK (add script before first PayPal button div, as per documentation)
    // Using components=buttons to support both subscriptions and one-time orders
    // vault=true required for subscriptions, intent not specified to allow both
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&vault=true&intent=subscription&components=buttons`;
    script.async = true;
    script.crossOrigin = "anonymous"; // Required to get detailed error messages instead of "Script error."
    script.onload = () => {
      setPaypalSdkLoaded(true);
    };
    script.onerror = () => {
      setPaypalSdkLoaded(false);
      // Don't throw - just mark as failed and let UI handle gracefully
    };
    // Insert before the subscription tiers card (before first PayPal button container)
    const subscriptionCard = document.querySelector('[data-subscription-tiers]');
    if (subscriptionCard && subscriptionCard.parentNode) {
      subscriptionCard.parentNode.insertBefore(script, subscriptionCard);
    } else {
      document.body.appendChild(script);
    }

    return () => {
      // Don't remove script on unmount - keep it for other components
      // The script should remain loaded as per PayPal best practices
    };
  }, [paypalClientId, paypalMounted]);

  // Countdown timer
  useEffect(() => {
    if (uploadLimits?.reset_countdown_seconds) {
      setCountdown(uploadLimits.reset_countdown_seconds);
      
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            // Refresh data when countdown reaches 0
            fetchSubscriptionData();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [uploadLimits?.reset_countdown_seconds, fetchSubscriptionData]);

  // Helper function to determine tier from roles
  const getTierFromRoles = (roles: string[] | undefined): string | null => {
    if (!roles || roles.length === 0) return null;

    // Tier priority: family > pro > starter > free
    // Check in order of priority (highest first)
    const tierRoles = ['family', 'pro', 'complimentary_pro', 'early_supporter', 'starter', 'complimentary_starter'];

    for (const tierRole of tierRoles) {
      if (roles.includes(tierRole)) {
        // Normalize: complimentary_pro → pro, early_supporter → lifetime_access
        if (tierRole.startsWith('complimentary_')) {
          return tierRole.replace('complimentary_', '');
        }
        if (tierRole === 'early_supporter') {
          return 'lifetime_access';
        }
        return tierRole;
      }
    }
    return null;
  };

  // Derived values
  // Check if user has an actual PayPal subscription
  const hasPayPalSubscription = !!subscriptionStatus?.paypal_subscription_id;

  // Derive tier from roles
  const roleBasedTier = getTierFromRoles(subscriptionStatus?.user_roles);

  // Determine if user has role-based access (role but no PayPal subscription)
  const hasRoleBasedAccess = !hasPayPalSubscription && roleBasedTier !== null && roleBasedTier !== 'free';

  // Use subscription tier if they have PayPal subscription, otherwise use role-based tier
  const tier = subscriptionStatus?.subscription_tier || 'free';
  const activeTier = tier.startsWith('complimentary_') ? tier.replace('complimentary_', '') : tier;
  const activeBillingCycle = subscriptionStatus?.billing_cycle || null;

  // hasPaidSubscription should ONLY be true if they have an actual PayPal subscription
  // Role-based users should NOT be considered as having a "paid subscription" for UI purposes
  const hasPaidSubscription = hasPayPalSubscription &&
                               activeTier !== 'free' &&
                               activeTier !== null &&
                               subscriptionStatus?.subscription_status === 'active';
  const hasPendingDowngrade = subscriptionStatus?.downgrade_pending === true;
  const isPendingCancellation = subscriptionStatus?.downgrade_pending &&
                                subscriptionStatus?.pending_tier === 'free' &&
                                subscriptionStatus?.subscription_status === 'active' &&
                                subscriptionStatus?.subscription_tier !== 'free';
  const hasPendingCancellationToFree = subscriptionStatus?.downgrade_pending &&
                                       subscriptionStatus?.pending_tier === 'free';

  // Check if current plan has free upgrade enabled
  const currentPlanWithFreeUpgrade = React.useMemo(() => {
    if (!subscriptionStatus?.paypal_subscription_id || !unifiedPlans.length) return null;
    
    // Find the plan that matches current subscription
    for (const unifiedPlan of unifiedPlans) {
      // Check monthly plans
      for (const plan of Object.values(unifiedPlan.monthly_plans || {})) {
        if (plan.paypal_plan_id === subscriptionStatus.paypal_subscription_id || 
            (plan.tier === activeTier && plan.billing_cycle === activeBillingCycle)) {
            return plan;
        }
      }
      // Check annual plans
      for (const plan of Object.values(unifiedPlan.annual_plans || {})) {
        if (plan.paypal_plan_id === subscriptionStatus.paypal_subscription_id || 
            (plan.tier === activeTier && plan.billing_cycle === activeBillingCycle)) {
            return plan;
        }
      }
    }
    return null;
  }, [subscriptionStatus, unifiedPlans, activeTier, activeBillingCycle]);

  // Check if user can use free upgrade
  const canUseFreeUpgrade = currentPlanWithFreeUpgrade && 
    activeTier !== 'pro' && 
    activeTier !== 'family' && 
    !activeTier.startsWith('complimentary_pro');

  // Calculate initial carousel index
  const getInitialCarouselIndex = React.useMemo(() => {
    if (!activeTier) return 0;
    
    if (activeTier === 'pro' || activeTier === 'starter' || 
        activeTier?.startsWith('complimentary_pro') || 
        activeTier?.startsWith('complimentary_starter')) {
      return 2; // Pro
    } else if (activeTier === 'free') {
      return 1; // Starter
    }
    return 0; // Free (default)
  }, [activeTier]);

  return {
    subscriptionStatus,
    subscriptionPlans,
    uploadLimits,
    features,
    lifetimeStatus,
    unifiedPlans,
    tierLimits,
    paypalClientId,
    paypalSdkLoaded,
    paypalMounted,
    loading,
    error,
    countdown,
    syncingSubscription,
    activeTab,
    tier,
    tierLabel: '', // Will be computed in component using TIER_LABELS
    activeTier,
    activeBillingCycle,
    hasPaidSubscription,
    hasRoleBasedAccess,
    hasPendingDowngrade,
    hasPendingCancellation: isPendingCancellation,
    hasPendingCancellationToFree,
    currentPlanWithFreeUpgrade,
    canUseFreeUpgrade,
    getInitialCarouselIndex,
    isFamilyMember,
    fetchSubscriptionData,
    handleTabChange,
  };
}
